import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, mergeProps as _mergeProps, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["data-tile-highlight", "data-item-highlight", "onContextmenu"]
const _hoisted_2 = ["title", "data-animation"]
const _hoisted_3 = { class: "text-[32px] leading-none" }
const _hoisted_4 = {
  key: 0,
  class: "absolute top-0.5 right-0.5 bg-black/70 text-white text-[10px] px-1 py-0.5 rounded font-bold"
}
const _hoisted_5 = {
  key: 1,
  class: "absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-sm overflow-hidden"
}
const _hoisted_6 = {
  key: 0,
  class: "tile-item ghost w-full h-full flex items-center justify-center"
}
const _hoisted_7 = { class: "text-[32px] leading-none" }
const _hoisted_8 = {
  key: 1,
  class: "tile-empty w-full h-full opacity-30"
}

import { computed, ref, watch } from 'vue'
import { game } from '../game/init'
import { Tile, type Item } from '../game'
import { usePointer } from '../lib/usePointer'

// Unified pointer handling: tap, hover, drag

export default /*@__PURE__*/_defineComponent({
  __name: 'GridPane',
  setup(__props) {

const pointer = usePointer<Tile>(
    (tile) => {
        if (tile.item) {
            game.ui.tap({ tile })
            game.ui.hover({ reason: 'tick', tile })
        }
    },
    (from, to) => {
        game.ui.drop({ from, to, item: from.item! })
    },
)

// Bridge pointer state to game events
watch(pointer.hovered, (tile, oldTile) => {
    if (tile) {
        game.ui.hover({ tile, reason: 'move' })
    } else if (oldTile) {
        game.ui.hover({ tile: null, reason: 'leave' })
    }
})

watch(pointer.dragging, (from) => {
    if (from) {
        game.ui.dragStart({ from })
    } else {
        game.ui.drop({ from: null, to: null, item: null })
    }
})

watch(pointer.dragOver, (over) => {
    game.ui.dragOver({ over: over ?? null, item: pointer.dragging.value?.item ?? null })
})

const gridRef = ref<HTMLElement | null>(null)

function getAnimationStyle(item: Item) {
    if (!item.animation || !gridRef.value) return {}

    const { from, to, type, startTick, endTick } = item.animation
    const duration = game.data.animations[type].duration

    const pos = item.position || from
    const span = Math.max(1, endTick - startTick)
    const progress = Math.min(1, Math.max(0, (game.state.tick - startTick) / span))

    return {
        '--pos-x': pos.x.toString(),
        '--pos-y': pos.y.toString(),
        '--from-x': from.x.toString(),
        '--from-y': from.y.toString(),
        '--to-x': to.x.toString(),
        '--to-y': to.y.toString(),
        '--duration': duration.toString(),
        '--progress': progress.toString(),
    }
}

const tiles = computed(() => Array.from(game.state.tiles.values()).sort((a, b) => {
    if (a.position.y !== b.position.y) return a.position.y - b.position.y
    return a.position.x - b.position.x
}))

watch(tiles, (newTiles, oldTiles) => {
    if (oldTiles) {
        for (const tile of oldTiles) {
            pointer.unregister(tile.key)
        }
    }
    for (const tile of newTiles) {
        pointer.register(tile.key, tile)
    }
}, { immediate: true })

const damagePreview = computed(() => {
    const plan = game.ui.state.activePlan
    const map = new Map<string, { damage: number; shade: number; clears: boolean }>()
    if (!plan?.explode) return map
    for (const a of plan.explode.affected) {
        map.set(a.tile.key, { damage: a.damage, shade: a.tile.shade, clears: a.clears })
    }
    return map
})

function getChargePercent(item: Item): number {
    if (!item.data.charge || item.charge === undefined) return 0
    return Math.min(100, (item.charge / item.data.charge.max) * 100)
}

function handleRightClick(tile: Tile) {
    if (tile.item) {
        game.ui.tap({ tile })
        game.ui.hover({ reason: 'tick', tile })
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "gridRef",
    ref: gridRef,
    class: "grid bg-gray-300 p-0.5 rounded max-w-fit relative",
    style: _normalizeStyle({
      gridTemplateColumns: `repeat(${_unref(game).state.size.x}, 1fr)`,
      gridTemplateRows: `repeat(${_unref(game).state.size.y}, 1fr)`,
      userSelect: 'none',
      '--tile-size': '52px',
    })
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tiles.value, (tile) => {
      return (_openBlock(), _createElementBlock("div", _mergeProps({
        key: tile.key,
        class: ["tile aspect-square min-w-[50px] min-h-[50px] bg-gray-100 border border-gray-300 rounded-sm relative transition-colors", {
                  'bg-white': tile.item && !tile.item.animation
              }],
        style: {
                  gridColumn: tile.position.x + 1,
                  gridRow: tile.position.y + 1
              },
        "data-tile-highlight": _unref(game).state.highlight?.[tile.key]?.tile || undefined,
        "data-item-highlight": _unref(game).state.highlight?.[tile.key]?.item || undefined
      }, { ref_for: true }, _unref(pointer).target(tile, tile.key), {
        onContextmenu: _withModifiers(($event: any) => (handleRightClick(tile)), ["prevent"])
      }), [
        _createCommentVNode(" Item "),
        (tile.item)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createCommentVNode(" The actual item - moves when dragging "),
              _createElementVNode("div", _mergeProps({ class: "tile-item w-full h-full flex items-center justify-center relative cursor-grab" }, { ref_for: true }, _unref(game).ui.canDrag(tile) ? _unref(pointer).source(tile) : {}, {
                title: tile.item.id,
                class: [
                          { 'opacity-50 cursor-not-allowed': tile.shade > 0 }
                      ],
                "data-animation": tile.item.animation?.type,
                style: tile.item.animation ? getAnimationStyle(tile.item) : {}
              }), [
                _createElementVNode("span", _hoisted_3, _toDisplayString(tile.item.data.display?.emoji ?? '📦'), 1 /* TEXT */),
                (tile.item.charge !== undefined && tile.shade === 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(tile.item.charge), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true),
                (tile.item.data.charge && tile.shade === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createElementVNode("div", {
                        class: "h-full bg-green-500",
                        style: _normalizeStyle({ width: getChargePercent(tile.item) + '%' })
                      }, null, 4 /* STYLE */)
                    ]))
                  : _createCommentVNode("v-if", true)
              ], 16 /* FULL_PROPS */, _hoisted_2),
              _createCommentVNode(" Ghost that stays in original position "),
              (_unref(game).state.dragging?.from && _unref(game).state.dragging.from.x === tile.position.x && _unref(game).state.dragging.from.y === tile.position.y)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createElementVNode("span", _hoisted_7, _toDisplayString(tile.item.data.display?.emoji ?? '📦'), 1 /* TEXT */)
                  ]))
                : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */))
          : (!tile.item)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8))
            : _createCommentVNode("v-if", true),
        _createCommentVNode(" Damage preview number "),
        (damagePreview.value.get(tile.key) && damagePreview.value.get(tile.key)!.shade > 0)
          ? (_openBlock(), _createElementBlock("span", {
              key: 2,
              class: _normalizeClass(["absolute bottom-0.5 left-0.5 z-30 bg-red-700/80 text-white text-[10px] leading-none px-1 py-0.5 rounded font-bold pointer-events-none", { '!bg-yellow-600/80': damagePreview.value.get(tile.key)!.clears }])
            }, _toDisplayString(damagePreview.value.get(tile.key)!.damage), 3 /* TEXT, CLASS */))
          : _createCommentVNode("v-if", true),
        _createCommentVNode(" Shade overlay "),
        (tile.shade > 0)
          ? (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: "shade-overlay pointer-events-none rounded-sm text-gray-400",
              style: _normalizeStyle({
                      backgroundColor: `rgba(20, 10, 30, ${Math.min(tile.shade / 50, 0.95)})`,
                  })
            }, _toDisplayString(tile.shade), 5 /* TEXT, STYLE */))
          : _createCommentVNode("v-if", true)
      ], 16 /* FULL_PROPS */, _hoisted_1))
    }), 128 /* KEYED_FRAGMENT */))
  ], 4 /* STYLE */))
}
}

})